/**
 * ★ THE AGREEMENTS, AS THIS FEATURE SHOWS THEM (design 25; design 24, T24's four places: the
 * application's last step, Payment details, this interstitial, and Settings' "Your agreements").
 *
 * WHAT A DOCUMENT IS CALLED. The cabinet names the documents it knows by their code, in its own
 * words (design 23: "author agreement", never the seeded "Creator programme terms"), and draws the
 * server's title only for a document it does not know yet - which a new row in `terms_document` is
 * until the cabinet learns its name.
 *
 * WHEN THE LIST CANNOT BE READ. `listAgreements` is design 25's; `getCurrentTerms` has always
 * answered for the author agreement alone. So a list that will not load is answered by the author
 * agreement's own read, as one document, and its acceptance goes through `acceptTerms` - the
 * interstitial keeps working, for the one document every server has.
 */
import { ENGLISH_RESOURCES, i18n } from '../../i18n';
import type { Agreement, AgreementList, TermsState } from './api/contract';

/** The author agreement's code (`terms_document`, seeded since migration 0003). */
export const AUTHOR_AGREEMENT_CODE = 'creator_program';

/** A document the cabinet names itself: `support:agreements.names.*` and `.accept.*`. */
type KnownDocument = keyof (typeof ENGLISH_RESOURCES)['support']['agreements']['names'];

function isKnown(code: string): code is KnownDocument {
  return Object.hasOwn(ENGLISH_RESOURCES.support.agreements.names, code);
}

/** The document's name in the cabinet's language - call it when rendering, never at module load. */
export function agreementName(agreement: Pick<Agreement, 'documentCode' | 'title'>): string {
  const code = agreement.documentCode;
  return isKnown(code) ? i18n.t(`agreements.names.${code}`, { ns: 'support' }) : agreement.title;
}

/** The words the document's one box says on the interstitial, as `agreementName` is read. */
export function acceptWords(agreement: Pick<Agreement, 'documentCode' | 'title'>): string {
  const code = agreement.documentCode;
  return isKnown(code)
    ? i18n.t(`agreements.accept.${code}`, { ns: 'support' })
    : i18n.t('agreements.acceptOther', { ns: 'support', title: agreement.title });
}

/**
 * The submission documents to accept now, in the documents' order - what the interstitial asks for:
 * each one owed, and each with a newer version waiting, which Settings' "Review and accept" sends
 * here (a version that changed nothing that matters is not owed, but it is still there to accept).
 */
export function owedSubmissionDocuments(list: AgreementList): readonly Agreement[] {
  return list.items.filter(
    (item) =>
      item.blocks === 'submission' && (item.owed || item.newerVersionWaiting) && item.live !== null,
  );
}

/** The author agreement's own read, as the one-document list it stands in for. */
export function listFromTerms(terms: TermsState): AgreementList {
  const acceptedAt = terms.acceptedAt;
  return {
    items: [
      {
        documentCode: AUTHOR_AGREEMENT_CODE,
        title: 'Author agreement',
        blocks: 'submission',
        live: {
          versionLabel: terms.versionLabel,
          effectiveFrom: terms.effectiveFrom,
          contentUrl: terms.contentUrl,
          isMaterialChange: terms.isMaterialChange,
        },
        accepted:
          terms.accepted && typeof acceptedAt === 'string'
            ? { versionLabel: terms.versionLabel, acceptedAt, contentUrl: terms.contentUrl }
            : null,
        owed: !terms.accepted,
        newerVersionWaiting: false,
      },
    ],
  };
}
